/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-useless-escape */
/* eslint-disable security/detect-unsafe-regex */
/* eslint-disable max-len */
import { FC, useState, memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { IconNzxtLogo } from '@nzxt/react-icons';
import { useTranslation as t, useFormatMessage } from '@utils/hooks';
import useUiStore, { getSetModalView } from '@stores/use-ui-store';
import Button from '@components/Button';
import ButtonLink from '@components/ButtonLink';
import Input from '@components/Input';
import Divider from '@components/Divider';
import useForgot from '@framework/auth/use-forgot';
import { getErrorMessageKey } from '@framework/utils/errors';
import { ROUTE_AUTH } from '@constants';
import * as styles from './styles';

type FormData = {
  email: string;
  serverError: string;
};

type Props = {
  pageType: string;
  isInverted?: boolean;
};

const ForgotView: FC<Props> = ({ pageType, isInverted }) => {
  const MESSAGE_INVALID_EMAIL = t('invalid_email');
  const MESSAGE_FORGOT_SUCCESS = t('user_forgot_success_message');

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const setModalView = useUiStore(getSetModalView);

  const forgot = useForgot();
  const f = useFormatMessage();

  const {
    register,
    handleSubmit,
    getValues,
    clearErrors,
    setError,
    formState,
    reset,
  } = useForm<FormData>({ mode: 'onBlur' });

  const { isDirty, isSubmitted, errors } = formState;

  useEffect(() => {
    if (isDirty && isSubmitted && errors.serverError) {
      clearErrors('serverError');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isSubmitted, errors]);

  const onSubmit = async (data: FormData): Promise<void> => {
    try {
      setLoading(true);
      await forgot({
        email: data.email,
      });
      setSuccessMessage(MESSAGE_FORGOT_SUCCESS);
      clearErrors();
      setLoading(false);
    } catch ({ errors }) {
      let errorMessage = f('error_generic');
      const errorMessageKey = getErrorMessageKey(errors);
      if (errorMessageKey) {
        window.heap?.track(errorMessageKey);
        errorMessage = f(errorMessageKey);
      }
      const formValues = getValues();
      // react-hook-form doesn't support dirtySinceLastSubmit so have to reset
      reset(formValues);
      setError('serverError', {
        type: 'error',
        message: errorMessage,
      });
      setLoading(false);
    }
  };
  return (
    <div
      className={
        pageType === 'standalone'
          ? styles.standaloneWrapper(isInverted)
          : styles.forgotWrapper(isInverted)
      }
    >
      <div className={styles.forgotTopCard}>
        <IconNzxtLogo className={styles.logo(isInverted)} />
        <div className={styles.forgotHeadingWrapper}>
          <h2 className={styles.forgotHeading(isInverted)}>
            {t('user_reset_password')}
          </h2>
          <p className={styles.forgotSubheading(isInverted)}>
            {t('user_forgot_password_subheading')}
          </p>
        </div>
      </div>

      <div className={styles.forgotBottomCard}>
        <div className={styles.forgotFormWrapper}>
          <form className={styles.forgotForm} onSubmit={handleSubmit(onSubmit)}>
            {(errors.serverError || successMessage) && (
              <div
                className={
                  errors.serverError
                    ? styles.errorWrapper
                    : styles.successWrapper
                }
              >
                <p className={styles.notification}>
                  {errors.serverError?.message || successMessage}
                </p>
              </div>
            )}
            <Input
              data-test-id="email"
              type="email"
              autoComplete="email"
              inputLabel={t('label_email_address')}
              labelFor="emailAddress"
              {...register('email', {
                required: true,
                // eslint-disable-next-line
                pattern:
                  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              })}
              requiredError={errors.email && errors.email.type === 'required'}
              patternError={
                errors.email &&
                errors.email.type === 'pattern' &&
                MESSAGE_INVALID_EMAIL
              }
              isInverted={isInverted}
            />
            <Button
              type="submit"
              buttonStyle="primary"
              loading={loading}
              className={styles.forgotButton}
            >
              {t('send')}
            </Button>

            <Divider text={t('or')} isInverted={isInverted} />
          </form>

          <div className={styles.forgotSignUpWrapper}>
            {pageType === 'standalone' ? (
              <ButtonLink
                href={`/${ROUTE_AUTH}/signin`}
                className={styles.forgotLinksBase}
                internalLink
              >
                {t('user_sign_in')}
              </ButtonLink>
            ) : (
              <Button
                type="button"
                buttonStyle="textButton"
                onClick={() => setModalView('LOGIN_VIEW')}
                className={styles.forgotLinksBase}
              >
                {t('user_sign_in')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ForgotView);
